import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { automationRulesService } from '../../../services/automationRulesService';

const RuleTestPanel = ({ rule, onClose }) => {
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState(null);

  const handleTest = async () => {
    try {
      setTesting(true);
      const results = await automationRulesService?.testRule(rule?.id);
      setTestResults(results);
    } catch (error) {
      console.error('Error testing rule:', error);
      alert('Failed to test rule');
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    handleTest();
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-1">
              Test Automation Rule
            </h2>
            <p className="text-sm text-muted-foreground">{rule?.ruleName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {testing ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Testing rule conditions...</p>
              </div>
            </div>
          ) : testResults ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon name="CheckCircle" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Test Successful</h3>
                    <p className="text-sm text-muted-foreground">
                      Found {testResults?.estimatedRecipients || 0} matching clients
                    </p>
                  </div>
                </div>
              </div>

              {/* Conditions */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Active Conditions</h3>
                <div className="space-y-2">
                  {testResults?.conditions?.map((condition, index) => (
                    <div key={index} className="bg-muted/30 border border-border rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Icon name="Filter" size={14} className="text-primary" />
                        <span className="font-medium text-foreground">{condition?.fieldName}</span>
                        <span className="text-muted-foreground">{condition?.operator}</span>
                        <span className="font-medium text-foreground">
                          {condition?.valueNumber || condition?.valueText || condition?.valueDate || 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sample Clients */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  Sample Matching Clients
                </h3>
                {testResults?.matchedClients?.length === 0 ? (
                  <div className="text-center py-8 bg-muted/30 rounded-lg">
                    <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">No clients match the current conditions</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {testResults?.matchedClients?.map((client) => (
                      <div
                        key={client?.id}
                        className="bg-background border border-border rounded-lg p-4 flex items-center justify-between"
                      >
                        <div>
                          <div className="font-medium text-foreground">{client?.nombre}</div>
                          <div className="text-sm text-muted-foreground">{client?.email}</div>
                        </div>
                        <div className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                          {client?.tipoCliente}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Execution Preview */}
              <div className="bg-success/5 border border-success/20 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Icon name="Info" size={16} className="text-success mt-0.5" />
                  <div className="text-sm text-foreground">
                    <strong>Execution Preview:</strong> When this rule runs, it will send emails to approximately{' '}
                    <strong>{testResults?.estimatedRecipients}</strong> recipients based on the current conditions.
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
              <p className="text-muted-foreground">Failed to load test results</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-6 flex justify-end gap-3">
          <button
            onClick={handleTest}
            disabled={testing}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth disabled:opacity-50 flex items-center gap-2"
          >
            {testing && <Icon name="Loader2" size={16} className="animate-spin" />}
            <span>Retest</span>
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RuleTestPanel;